import Button from 'Components/Atoms/Button/Button';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import useDrawerStack from 'Hooks/useDrawerStack';
import useInvoiceSeries from 'Hooks/useInvoiceSeries';
import { useTranslation } from 'react-i18next';
import { DRAWER_STACK, INVOICE_SUBTYPES } from 'Utils/constants';
import { createInvoiceSerie, deleteInvoiceSerie, updateInvoiceSerie } from 'Services/invoices';
import Loading from 'Components/Atoms/Loading/Loading';
import { useQueryClient } from '@tanstack/react-query';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import Icon from 'Components/Atoms/Icon/Icon';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager,
    offset,
    flip,
    autoUpdate,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';

function InvoiceSerieRow({ serie, onEdit, onDelete, className }) {
    const { name, invoice_subtype, next_identifier, last_generation_date } = serie;
    const enableDelete = !last_generation_date;
    const [open, setOpen] = useState(false);

    const { t } = useTranslation('invoices');

    const INVOICE_SUBTYPES_LABELS = {
        [INVOICE_SUBTYPES.STANDARD]: t('invoice-standard'),
        [INVOICE_SUBTYPES.STANDARD_CORRECTIVE]: t('invoice-standard-corrective'),
        [INVOICE_SUBTYPES.SIMPLIFIED]: t('invoice-simplified'),
        [INVOICE_SUBTYPES.SIMPLIFIED_CORRECTIVE]: t('invoice-simplified-corrective'),
        [INVOICE_SUBTYPES.QUOTE]: t('invoice-quote'),
    };

    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        placement: 'bottom-end',
        middleware: [
            flip(),
            offset({
                mainAxis: -8,
                crossAxis: -4,
            }),
        ],
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
    ]);

    return (
        <div
            className={cn(
                'grid grid-cols-[2fr_1fr_auto] sm:grid-cols-[1.5fr_1fr_1fr_auto] gap-2 items-center py-0.5',
                className,
            )}
        >
            <button
                type="button"
                onClick={() => onEdit(serie)}
                className="pl-3 text-sm flex items-center gap-2 grow min-w-0 text-gray-900"
            >
                <Icon name="series" color="currentColor" className="shrink-0" size={20} />
                <div className="font-semibold min-w-0 truncate">{name}</div>
            </button>
            <div className="text-xs truncate text-gray-500">{INVOICE_SUBTYPES_LABELS[invoice_subtype]}</div>
            <div className="text-xs truncate text-gray-500 hidden sm:block">
                {next_identifier === null ? (
                    <Tooltip label={t('max-series-reached')}>
                        <span className="inline-flex">
                            <Icon
                                size={20}
                                color="currentColor"
                                className="shrink-0 text-gray-400"
                                name="exclamation-triangle"
                            />
                        </span>
                    </Tooltip>
                ) : (
                    next_identifier
                )}
            </div>

            <div className="relative flex items-center">
                <div className="flex justify-center items-center w-[20px] -mr-1">
                    {serie.is_default && (
                        <Tooltip label={t('default-serie')}>
                            <span className="text-dark-blue opacity-40 inline-flex">
                                <Icon name="circle-check" size={20} color="currentColor" />
                            </span>
                        </Tooltip>
                    )}
                </div>

                <IconButton
                    ref={refs.setReference}
                    {...getReferenceProps()}
                    icon={<Icon name="dots-vertical" />}
                    variant="ghost"
                    onClick={() => setOpen(!open)}
                    className={cn(open && 'text-dark-blue')}
                />

                <AnimatePresence>
                    {open && (
                        <FloatingFocusManager context={context}>
                            <motion.div
                                ref={refs.setFloating}
                                style={{
                                    ...floatingStyles,
                                    position: strategy,
                                    top: y ?? 0,
                                    left: x ?? 0,
                                }}
                                className="rounded-md bg-white ring ring-black/5 shadow-sm z-dropdown min-w-[100px]"
                                {...getFloatingProps()}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                            >
                                <div className="p-1 grid">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onEdit(serie);
                                            setOpen(false);
                                        }}
                                        className="text-sm px-2 py-1.5 rounded-sm transition-colors text-left font-medium hover:bg-gray-100"
                                    >
                                        {t('edit')}
                                    </button>

                                    {enableDelete && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onDelete(serie);
                                                setOpen(false);
                                            }}
                                            className="text-sm px-2 py-1.5 rounded-sm transition-colors text-left font-medium hover:bg-gray-100 text-danger"
                                        >
                                            {t('delete')}
                                        </button>
                                    )}

                                    {!enableDelete && (
                                        <Tooltip
                                            label={t('cannot-delete-serie-in-use')}
                                            className="[&>*]:max-w-[160px]!"
                                        >
                                            <div className="text-sm px-2 py-1.5 rounded-sm text-left font-medium text-gray-400 cursor-not-allowed flex items-center gap-1.5">
                                                {t('delete')}
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </motion.div>
                        </FloatingFocusManager>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

InvoiceSerieRow.propTypes = {
    serie: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        invoice_subtype: PropTypes.string.isRequired,
        next_identifier: PropTypes.string,
        last_generation_date: PropTypes.any,
        is_default: PropTypes.bool.isRequired,
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    className: PropTypes.string,
};

export default function InvoicesInvoicingSeriesDrawer() {
    const { series, isLoading, isError } = useInvoiceSeries();
    const { open } = useDrawerStack();
    const { t } = useTranslation('invoices');

    const queryClient = useQueryClient();

    const handleAddSerie = () => {
        open(DRAWER_STACK.INVOICE_SERIES_EDITOR, {
            title: t('new-serie'),
            serie: {
                is_default: true,
            },
            enableFirstValueThisYear: true,
            onSubmit: async (data) => {
                await createInvoiceSerie(data);
                await queryClient.invalidateQueries(['invoices', 'series']);
            },
        });
    };

    const handleEditSerie = (serie) => {
        open(DRAWER_STACK.INVOICE_SERIES_EDITOR, {
            title: t('edit-serie'),
            serie,
            onSubmit: async (data) => {
                await updateInvoiceSerie(serie.id, data);
                await queryClient.invalidateQueries(['invoices', 'series']);
            },
        });
    };

    const handleDeleteSerie = (serie) => {
        open(DRAWER_STACK.CONFIRMATION_DIALOG, {
            title: t('delete-serie'),
            description: t('delete-serie-description', { name: serie.name }),
            onSubmit: async () => {
                await deleteInvoiceSerie(serie.id);
                await queryClient.invalidateQueries(['invoices', 'series']);
            },
        });
    };

    return (
        <div className="py-4">
            <div className="rounded-lg border border-gray-200 bg-white">
                {isLoading && (
                    <div className="py-4 flex justify-center">
                        <Loading />
                    </div>
                )}

                {isError && (
                    <div className="py-9 px-2 text-center text-sm text-gray-500 border-b border-gray-200">
                        {t('error-loading-series')}
                    </div>
                )}

                {!isLoading && series.length === 0 && (
                    <div className="py-9 px-2 text-center text-sm text-gray-500 border-b border-gray-200">
                        {t('no-series')}
                    </div>
                )}

                {!isLoading && (
                    <div className="grid">
                        {series.map((serie) => (
                            <InvoiceSerieRow
                                className="border-b border-gray-200 "
                                key={serie.id}
                                serie={serie}
                                onEdit={() => handleEditSerie(serie)}
                                onDelete={() => handleDeleteSerie(serie)}
                            />
                        ))}
                    </div>
                )}

                <div className="py-0.5">
                    <Button variant="ghost" className="text-sm px-3" onClick={handleAddSerie}>
                        <OutlineAdd />
                        {t('add-serie')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
